import { getWeekStartDate, getWeekNumber } from '../utils/helpers';
import { SCHEDULE_START_MINUTE, TOTAL_SCHEDULE_MINUTES, timeSlots } from '../config/constants';
import ScheduleEntry, { WeekConflictInfo } from '../models/ScheduleEntry';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import Preference from '../models/Preference';
import AcademicYearService from './AcademicYearService';
import ScheduleItemsIndex from './ScheduleItemsIndex';
import logger from '../utils/logger';

export type Severity = 'green' | 'orange' | 'red';
export type StatusConstant = 'free' | 'prefer_away' | 'cannot';

export interface ConflictReason {
    reason: string;
    severity: Severity;
    week?: number;
    year?: number;
}

export interface AvailabilityResult {
    status: Severity;
    reasons: ConflictReason[];
    teacherStatus: StatusConstant;
    groupStatus: StatusConstant;
    isRoomOccupied: boolean;
    isRoomValidForSubject: boolean;
    isHoliday: boolean;
    overallStatus: Severity;
    conflictReasons: ConflictReason[];
    conflictsByWeek: Map<string, WeekConflictInfo>;
}

export interface WeeklyAvailability {
    weekStartDate: Date;
    weekNumber: number;
    availabilityByDay: {
        [key: string]: {
            [timeSlot: string]: AvailabilityResult;
        };
    };
}

class AvailabilityService {
    private readonly STATUS_CONSTANTS = {
        FREE: 'free',
        PREFER_AWAY: 'prefer_away',
        CANNOT: 'cannot'
    } as const;

    private scheduleItemsIndex: ScheduleItemsIndex = new ScheduleItemsIndex();
    
    // Lookup maps for performance optimization
    private teacherLookupMap: Map<string, Teacher> = new Map();
    private groupLookupMap: Map<string, Group> = new Map();
    private subjectLookupMap: Map<string, Subject> = new Map();
    private subjectIdLookupMap: Map<string, Subject> = new Map();
    private lastLookupUpdate: number = 0;
    private readonly LOOKUP_CACHE_DURATION = 30000; // 30 seconds

    /**
     * Updates lookup maps for performance optimization
     */
    private updateLookupMaps(allTeachers: Teacher[], allGroups: Group[], allSubjects: Subject[]): void {
        const now = Date.now();
        if (now - this.lastLookupUpdate < this.LOOKUP_CACHE_DURATION) {
            return; // Use cached lookups
        }

        this.teacherLookupMap.clear();
        this.groupLookupMap.clear();
        this.subjectLookupMap.clear();
        this.subjectIdLookupMap.clear();

        // Build teacher lookup map
        for (const teacher of allTeachers) {
            if (teacher.name) {
                this.teacherLookupMap.set(teacher.name, teacher);
            }
        }

        // Build group lookup map
        for (const group of allGroups) {
            if (group.name) {
                this.groupLookupMap.set(group.name, group);
            }
        }

        // Build subject lookup maps
        for (const subject of allSubjects) {
            if (subject.name) {
                this.subjectLookupMap.set(subject.name, subject);
            }
            if (subject.id) {
                this.subjectIdLookupMap.set(subject.id, subject);
            }
        }

        console.debug(`[AvailabilityService] Updated lookup maps: ${this.subjectLookupMap.size} subjects by name, ${this.subjectIdLookupMap.size} subjects by ID`);

        this.lastLookupUpdate = now;
    }

    /**
     * Shared implementation for a single week, toggling spatial index usage.
     */
    private computeAvailabilityForWeek(
        scheduleEntry: ScheduleEntry,
        targetRoom: string,
        targetDay: string,
        targetTimeMinutes: number,
        durationMinutes: number,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[],
        targetWeekNumber: number,
        targetYear: number,
        options: { useSpatialIndex?: boolean } = {}
    ): AvailabilityResult {
        const useSpatialIndex = !!options.useSpatialIndex;

        const result: AvailabilityResult = {
            teacherStatus: this.STATUS_CONSTANTS.FREE,
            groupStatus: this.STATUS_CONSTANTS.FREE,
            isRoomOccupied: false,
            isRoomValidForSubject: true,
            isHoliday: false,
            overallStatus: 'green',
            status: 'green',
            reasons: [],
            conflictReasons: [],
            conflictsByWeek: new Map()
        };

        // --- 1. Teacher Availability (preferences) ---
        if (scheduleEntry.teacher) {
            const teacher = this.teacherLookupMap.get(scheduleEntry.teacher) || allTeachers.find(t => t.name === scheduleEntry.teacher);
            const teacherPreferences = teacher?.preferences || [];
            result.teacherStatus = this.getStatusForSlot(targetDay, targetTimeMinutes, teacherPreferences);

            if (result.teacherStatus === this.STATUS_CONSTANTS.CANNOT) {
                result.overallStatus = 'red';
                result.status = 'red';
                result.reasons.push({
                    reason: `Teacher (${scheduleEntry.teacher}) unavailable.`,
                    severity: 'red',
                    week: targetWeekNumber,
                    year: targetYear
                });
            } else if (result.teacherStatus === this.STATUS_CONSTANTS.PREFER_AWAY) {
                if (result.overallStatus !== 'red') {
                    result.overallStatus = 'orange';
                    result.status = 'orange';
                }
                result.reasons.push({
                    reason: `Teacher (${scheduleEntry.teacher}) prefers away.`,
                    severity: 'orange',
                    week: targetWeekNumber,
                    year: targetYear
                });
            }
        }

        // --- 2. Group Availability (preferences) ---
        if (scheduleEntry.groups?.length) {
            for (const groupItem of scheduleEntry.groups) {
                const groupObj = typeof groupItem === 'object' && groupItem.id
                    ? allGroups.find(g => g.id === groupItem.id)
                    : typeof groupItem === 'number'
                        ? allGroups.find(g => g.id === groupItem)
                        : allGroups.find(g => g.name === groupItem || g.group_code === groupItem);

                const groupDisplayName = typeof groupItem === 'object'
                    ? (groupItem.name || groupItem.group_code || groupItem.id)
                    : groupItem;

                const groupPreferences = groupObj?.preferences || [];
                const currentGroupStatus = this.getStatusForSlot(targetDay, targetTimeMinutes, groupPreferences);

                if (currentGroupStatus === this.STATUS_CONSTANTS.CANNOT) {
                    result.overallStatus = 'red';
                    result.status = 'red';
                    result.groupStatus = this.STATUS_CONSTANTS.CANNOT;
                    result.reasons.push({
                        reason: `Group (${groupDisplayName}) unavailable.`,
                        severity: 'red',
                        week: targetWeekNumber,
                        year: targetYear
                    });
                    break;
                } else if (currentGroupStatus === this.STATUS_CONSTANTS.PREFER_AWAY && result.groupStatus === this.STATUS_CONSTANTS.FREE) {
                    result.groupStatus = this.STATUS_CONSTANTS.PREFER_AWAY;
                    if (result.overallStatus === 'green') {
                        result.overallStatus = 'orange';
                        result.status = 'orange';
                        result.reasons.push({
                            reason: `Group (${groupDisplayName}) prefers away.`,
                            severity: 'orange',
                            week: targetWeekNumber,
                            year: targetYear
                        });
                    }
                }
            }
        }

        // --- 3. Room Occupancy ---
        const roomOccupancy = useSpatialIndex
            ? this.scheduleItemsIndex.checkRoomOccupancy(
                targetRoom,
                targetDay,
                targetTimeMinutes,
                durationMinutes,
                scheduleEntry.id,
                targetWeekNumber,
                targetYear
            )
            : this.checkRoomOccupancyForWeek(
                targetRoom,
                targetDay,
                targetTimeMinutes,
                durationMinutes,
                scheduleEntry.id,
                allScheduleItems,
                targetWeekNumber,
                targetYear
            );

        if (roomOccupancy.isOccupied) {
            result.overallStatus = 'red';
            result.status = 'red';
            result.isRoomOccupied = true;
            const conflictTime = roomOccupancy.conflictingItem?.timeRangeDisplay();
            result.reasons.push({
                reason: `Room (${targetRoom}) occupied by ${roomOccupancy.conflictingItem?.course || roomOccupancy.conflictingItem?.type || 'another item'} ${conflictTime}.`,
                severity: 'red',
                week: targetWeekNumber,
                year: targetYear
            });
        }

        // --- 4. Group Schedule Conflicts ---
        if (scheduleEntry.groups?.length) {
            const groupConflicts = useSpatialIndex
                ? this.scheduleItemsIndex.checkGroupScheduleConflicts(
                    scheduleEntry.groups as (string | number | { id: number; name?: string; group_code?: string; })[],
                    targetDay,
                    targetTimeMinutes,
                    durationMinutes,
                    scheduleEntry.id,
                    targetWeekNumber,
                    targetYear
                )
                : this.checkGroupScheduleConflictsForWeek(
                    scheduleEntry,
                    targetDay,
                    targetTimeMinutes,
                    durationMinutes,
                    allScheduleItems,
                    targetWeekNumber,
                    targetYear
                );

            if (groupConflicts.length > 0) {
                result.overallStatus = 'red';
                result.status = 'red';
                result.reasons.push(...groupConflicts.map(conflict => ({
                    ...conflict,
                    week: targetWeekNumber,
                    year: targetYear
                })));
            }
        }

        // --- 5. Teacher Schedule Conflicts ---
        if (scheduleEntry.teacher) {
            const teacherConflicts = useSpatialIndex
                ? this.scheduleItemsIndex.checkTeacherScheduleConflicts(
                    scheduleEntry.teacher,
                    targetDay,
                    targetTimeMinutes,
                    durationMinutes,
                    scheduleEntry.id,
                    targetWeekNumber,
                    targetYear
                )
                : this.checkTeacherScheduleConflictsForWeek(
                    scheduleEntry,
                    targetDay,
                    targetTimeMinutes,
                    durationMinutes,
                    allScheduleItems,
                    targetWeekNumber,
                    targetYear
                );

            if (teacherConflicts.length > 0) {
                result.overallStatus = 'red';
                result.status = 'red';
                result.reasons.push(...teacherConflicts.map(conflict => ({
                    ...conflict,
                    week: targetWeekNumber,
                    year: targetYear
                })));
            }
        }

        // --- 6. Subject-Room Constraint ---
        if (scheduleEntry.course) {
            const subject = this.subjectLookupMap.get(scheduleEntry.course);
            if (subject) {
                result.isRoomValidForSubject = this.checkSubjectRoomConstraint(subject.id, targetRoom);
                if (!result.isRoomValidForSubject) {
                    if (result.overallStatus !== 'red') {
                        result.overallStatus = 'orange';
                        result.status = 'orange';
                    }
                    result.reasons.push({
                        reason: `Room (${targetRoom}) not suitable for subject (${scheduleEntry.course}).`,
                        severity: 'orange',
                        week: targetWeekNumber,
                        year: targetYear
                    });
                }
            }
        }

        // --- 7. Holiday Check ---
        if (result.overallStatus !== 'red') {
            const holidayCheck = this.checkHolidayAvailability(targetDay, targetWeekNumber, targetYear);

            if (holidayCheck.severity === 'red' && holidayCheck.conflictReason) {
                result.overallStatus = 'red';
                result.status = 'red';
                result.isHoliday = true;
                result.reasons.push({
                    ...holidayCheck.conflictReason,
                    week: targetWeekNumber,
                    year: targetYear
                });
            } else if (holidayCheck.severity === 'orange' && holidayCheck.conflictReason) {
                result.overallStatus = 'orange';
                result.status = 'orange';
                result.isHoliday = true;
                result.reasons.push({
                    ...holidayCheck.conflictReason,
                    week: targetWeekNumber,
                    year: targetYear
                });
            }
        }

        // Deduplicate conflict reasons before returning
        const deduplicatedReasons = this.deduplicateConflictReasons(result.reasons);
        result.reasons = deduplicatedReasons;
        result.conflictReasons = deduplicatedReasons;

        if (deduplicatedReasons.length > 0 || result.overallStatus !== 'green') {
            result.conflictsByWeek.set(`${targetWeekNumber}-${targetYear}`, {
                week: targetWeekNumber,
                year: targetYear,
                status: result.overallStatus,
                reasons: deduplicatedReasons,
                lastCalculated: new Date()
            });
        }

        return result;
    }

    /**
     * Check availability for a schedule entry across all its active weeks (baseline path).
     */
    checkAvailability(
        scheduleEntry: ScheduleEntry,
        targetRoom: string,
        targetDay: string,
        targetTimeMinutes: number,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[]
    ): AvailabilityResult {
        // Update lookup maps for fast access
        this.updateLookupMaps(allTeachers, allGroups, allSubjects);

        const conflictReasons: ConflictReason[] = [];
        const conflictsByWeek = new Map<string, WeekConflictInfo>();

        // --- Input Validation ---
        if (!scheduleEntry || !scheduleEntry.id || !scheduleEntry.timeStart || !scheduleEntry.timeEnd || targetTimeMinutes === null || !targetDay || !targetRoom) {
            const freeStatus = this.STATUS_CONSTANTS.FREE;
            conflictReasons.push({ reason: 'Invalid input for availability check.', severity: 'red' as Severity });

            return {
                teacherStatus: freeStatus,
                groupStatus: freeStatus,
                isRoomOccupied: false,
                isRoomValidForSubject: true,
                isHoliday: false,
                overallStatus: 'red',
                status: 'red',
                reasons: conflictReasons,
                conflictReasons: conflictReasons,
                conflictsByWeek
            };
        }

        const { durationMinutes } = scheduleEntry.parseTime();
        if (durationMinutes === null || durationMinutes <= 0) {
            const freeStatus = this.STATUS_CONSTANTS.FREE;
            conflictReasons.push({ reason: 'Invalid item duration.', severity: 'red' as Severity });

            return {
                teacherStatus: freeStatus,
                groupStatus: freeStatus,
                isRoomOccupied: false,
                isRoomValidForSubject: true,
                isHoliday: false,
                overallStatus: 'red',
                status: 'red',
                reasons: conflictReasons,
                conflictReasons: conflictReasons,
                conflictsByWeek
            };
        }

        // Get active weeks for this entry (use current year +/- 1 as range)
        const currentYear = new Date().getFullYear();
        const activeWeeks = scheduleEntry.getActiveWeeksSync(currentYear - 1, currentYear + 1);

        for (const { week, year } of activeWeeks) {
            const weekResult = this.computeAvailabilityForWeek(
                scheduleEntry,
                targetRoom,
                targetDay,
                targetTimeMinutes,
                durationMinutes,
                allScheduleItems,
                allTeachers,
                allGroups,
                allSubjects,
                week,
                year,
                { useSpatialIndex: false }
            );

            const weekConflict = weekResult.conflictsByWeek.get(`${week}-${year}`);
            if (weekConflict) {
                conflictsByWeek.set(`${week}-${year}`, weekConflict);
                conflictReasons.push(...(weekConflict.reasons || []));
            }
        }

        // Derive overall status and aggregates from per-week results
        let overallStatus: Severity = 'green';
        let teacherStatus: StatusConstant = this.STATUS_CONSTANTS.FREE;
        let groupStatus: StatusConstant = this.STATUS_CONSTANTS.FREE;
        let isRoomOccupied = false;
        let isRoomValidForSubject = true;
        let isHoliday = false;

        conflictsByWeek.forEach((weekConflict) => {
            if (weekConflict.status === 'red') {
                overallStatus = 'red';
            } else if (weekConflict.status === 'orange' && overallStatus !== 'red') {
                overallStatus = 'orange';
            }

            if (weekConflict.reasons.some(r => r.reason.includes('Teacher') && r.severity === 'red')) {
                teacherStatus = this.STATUS_CONSTANTS.CANNOT;
            } else if (weekConflict.reasons.some(r => r.reason.includes('Teacher') && r.severity === 'orange') && teacherStatus === this.STATUS_CONSTANTS.FREE) {
                teacherStatus = this.STATUS_CONSTANTS.PREFER_AWAY;
            }

            if (weekConflict.reasons.some(r => r.reason.includes('Group') && r.severity === 'red')) {
                groupStatus = this.STATUS_CONSTANTS.CANNOT;
            } else if (weekConflict.reasons.some(r => r.reason.includes('Group') && r.severity === 'orange') && groupStatus === this.STATUS_CONSTANTS.FREE) {
                groupStatus = this.STATUS_CONSTANTS.PREFER_AWAY;
            }

            if (weekConflict.reasons.some(r => r.reason.includes('Room') && r.reason.includes('occupied'))) {
                isRoomOccupied = true;
            }

            if (weekConflict.reasons.some(r => r.reason.includes('not suitable for subject'))) {
                isRoomValidForSubject = false;
            }

            if (weekConflict.reasons.some(r => r.reason.includes('Holiday') || r.reason.includes('break') || r.reason.includes('vacation'))) {
                isHoliday = true;
            }
        });

        const deduplicatedReasons = this.deduplicateConflictReasons(conflictReasons);

        return {
            teacherStatus,
            groupStatus,
            isRoomOccupied,
            isRoomValidForSubject,
            isHoliday,
            overallStatus,
            status: overallStatus,
            reasons: deduplicatedReasons,
            conflictReasons: deduplicatedReasons,
            conflictsByWeek
        };
    }

    /**
     * Ultra-optimized availability check for drag operations
     * Uses lookup maps and minimal object creation for maximum performance
     */
    checkAvailabilityUltraOptimized(
        scheduleEntry: ScheduleEntry,
        targetRoom: string,
        targetDay: string,
        targetTimeMinutes: number,
        durationMinutes: number,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[],
        targetWeekNumber: number,
        targetYear: number
    ): AvailabilityResult {
        if (!scheduleEntry || !scheduleEntry.id || !scheduleEntry.timeStart || !scheduleEntry.timeEnd || targetTimeMinutes === null || !targetDay || !targetRoom) {
            const conflictReasons = [{ reason: 'Invalid input for availability check.', severity: 'red' as Severity }];
            return {
                teacherStatus: this.STATUS_CONSTANTS.FREE,
                groupStatus: this.STATUS_CONSTANTS.FREE,
                isRoomOccupied: false,
                isRoomValidForSubject: true,
                isHoliday: false,
                overallStatus: 'red',
                status: 'red',
                reasons: conflictReasons,
                conflictReasons,
                conflictsByWeek: new Map()
            };
        }

        this.updateLookupMaps(allTeachers, allGroups, allSubjects);
        return this.computeAvailabilityForWeek(
            scheduleEntry,
            targetRoom,
            targetDay,
            targetTimeMinutes,
            durationMinutes,
            allScheduleItems,
            allTeachers,
            allGroups,
            allSubjects,
            targetWeekNumber,
            targetYear,
            { useSpatialIndex: true }
        );
    }

    /**
     * Optimized availability check for a single week using spatial indexing
     * This method is optimized for drag operations where we need to check many slots quickly
     */
    checkAvailabilityOptimized(
        scheduleEntry: ScheduleEntry,
        targetRoom: string,
        targetDay: string,
        targetTimeMinutes: number,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[],
        targetWeekNumber: number,
        targetYear: number
    ): AvailabilityResult {
        if (!scheduleEntry || !scheduleEntry.id || !scheduleEntry.timeStart || !scheduleEntry.timeEnd || targetTimeMinutes === null || !targetDay || !targetRoom) {
            const conflictReasons = [{ reason: 'Invalid input for availability check.', severity: 'red' as Severity }];
            return {
                teacherStatus: this.STATUS_CONSTANTS.FREE,
                groupStatus: this.STATUS_CONSTANTS.FREE,
                isRoomOccupied: false,
                isRoomValidForSubject: true,
                isHoliday: false,
                overallStatus: 'red',
                status: 'red',
                reasons: conflictReasons,
                conflictReasons,
                conflictsByWeek: new Map()
            };
        }

        this.updateLookupMaps(allTeachers, allGroups, allSubjects);

        const { durationMinutes } = scheduleEntry.parseTime();
        if (durationMinutes === null || durationMinutes <= 0) {
            const freeStatus = this.STATUS_CONSTANTS.FREE;
            const conflictsByWeek = new Map<string, WeekConflictInfo>();
            const conflictReasons: ConflictReason[] = [{ reason: 'Invalid item duration.', severity: 'red' as Severity }];
            return {
                teacherStatus: freeStatus,
                groupStatus: freeStatus,
                isRoomOccupied: false,
                isRoomValidForSubject: true,
                isHoliday: false,
                overallStatus: 'red',
                status: 'red',
                reasons: conflictReasons,
                conflictReasons: conflictReasons,
                conflictsByWeek
            };
        }

        return this.computeAvailabilityForWeek(
            scheduleEntry,
            targetRoom,
            targetDay,
            targetTimeMinutes,
            durationMinutes,
            allScheduleItems,
            allTeachers,
            allGroups,
            allSubjects,
            targetWeekNumber,
            targetYear,
            { useSpatialIndex: true }
        );
    }

    /**
     * Builds the spatial index for optimized availability checking
     * Call this once before doing multiple availability checks for the same week
     */
    buildSpatialIndexForWeek(
        allScheduleItems: ScheduleEntry[],
        targetWeekNumber: number,
        targetYear: number
    ): void {
        this.scheduleItemsIndex.buildIndexForWeekSync(allScheduleItems, targetWeekNumber, targetYear);
    }

    /**
     * Clears the spatial index and lookup maps
     */
    clearSpatialIndex(): void {
        this.scheduleItemsIndex.clear();
        // Optional: clear lookup maps to free memory (they'll be rebuilt on next use)
        // this.teacherLookupMap.clear();
        // this.groupLookupMap.clear();
        // this.subjectLookupMap.clear();
        // this.subjectIdLookupMap.clear();
    }

    /**
     * Check if a room is occupied during a given time slot for a specific week
     */
    private checkRoomOccupancyForWeek(
        targetRoom: string,
        targetDay: string,
        targetTimeMinutes: number,
        durationMinutes: number,
        draggedItemId: string,
        allScheduleItems: ScheduleEntry[],
        targetWeekNumber: number,
        targetYear: number
    ): { isOccupied: boolean; conflictingItem: ScheduleEntry | null } {
        if (!targetRoom || !targetDay || targetTimeMinutes === null || durationMinutes === null) {
            return { isOccupied: false, conflictingItem: null };
        }

        const targetStart = targetTimeMinutes;
        const targetEnd = targetTimeMinutes + durationMinutes;

        for (const item of allScheduleItems) {
            if (!item || item.id === draggedItemId || !item.room || !item.day || !item.isTimeValid) {
                continue;
            }

            // Check if the item is active for the target week
            const isActive = item.isActiveForWeekSync 
                ? item.isActiveForWeekSync(targetWeekNumber, targetYear)
                : true; // Fallback if sync method not available
            
            if (!isActive) {
                continue;
            }

            if (item.room === targetRoom && item.day === targetDay) {
                const itemTime = item.parseTime();
                if (itemTime.startMinutes !== null && itemTime.endMinutes !== null) {
                    if (targetStart < itemTime.endMinutes && targetEnd > itemTime.startMinutes) {
                        return { isOccupied: true, conflictingItem: item };
                    }
                }
            }
        }

        return { isOccupied: false, conflictingItem: null };
    }

    /**
     * Check if any group in the schedule entry has a conflicting class at the same time for a specific week
     */
    private checkGroupScheduleConflictsForWeek(
        scheduleEntry: ScheduleEntry,
        targetDay: string,
        targetTimeMinutes: number,
        durationMinutes: number,
        allScheduleItems: ScheduleEntry[],
        targetWeekNumber: number,
        targetYear: number
    ): ConflictReason[] {
        const conflicts: ConflictReason[] = [];
        
        if (!scheduleEntry.groups || scheduleEntry.groups.length === 0) {
            return conflicts;
        }

        const targetStart = targetTimeMinutes;
        const targetEnd = targetTimeMinutes + durationMinutes;

        // Check each group for conflicts
        for (const group of scheduleEntry.groups) {
            // Extract the group identifier for comparison and display
            const groupIdentifier = typeof group === 'object' ? 
                (group.name || group.group_code || group.id) : group;
                
            for (const item of allScheduleItems) {
                // Skip the current item being checked
                if (!item || item.id === scheduleEntry.id || !item.groups || item.groups.length === 0) {
                    continue;
                }

                // Check if the item is on the same day
                if (item.day !== targetDay) {
                    continue;
                }

                // Check if the item is active for the target week
                const isActive = item.isActiveForWeekSync 
                    ? item.isActiveForWeekSync(targetWeekNumber, targetYear)
                    : true; // Fallback if sync method not available
                
                if (!isActive) {
                    continue;
                }

                // Check if this item has the same group
                const hasConflictingGroup = item.groups.some(itemGroup => {
                    // Handle both string and object group representations
                    const itemGroupName = typeof itemGroup === 'object' ? 
                        (itemGroup.name || itemGroup.group_code || itemGroup.id) : itemGroup;
                    return itemGroupName === groupIdentifier;
                });

                if (hasConflictingGroup) {
                    // Check for time overlap
                    const itemTime = item.parseTime();
                    if (itemTime.startMinutes !== null && itemTime.endMinutes !== null) {
                        if (targetStart < itemTime.endMinutes && targetEnd > itemTime.startMinutes) {
                            const conflictTime = item.timeRangeDisplay();
                            const conflictCourse = item.course || item.type || 'another class';
                            const conflictRoom = item.room || 'unknown room';
                            
                            conflicts.push({
                                reason: `Group (${groupIdentifier}) has conflicting class: ${conflictCourse} in ${conflictRoom} ${conflictTime}, week ${targetWeekNumber}`,
                                severity: 'red'
                            });
                        }
                    }
                }
            }
        }

        return conflicts;
    }

    /**
     * Check if the teacher has a conflicting class at the same time for a specific week
     */
    private checkTeacherScheduleConflictsForWeek(
        scheduleEntry: ScheduleEntry,
        targetDay: string,
        targetTimeMinutes: number,
        durationMinutes: number,
        allScheduleItems: ScheduleEntry[],
        targetWeekNumber: number,
        targetYear: number
    ): ConflictReason[] {
        const conflicts: ConflictReason[] = [];
        
        if (!scheduleEntry.teacher) {
            return conflicts;
        }

        const targetStart = targetTimeMinutes;
        const targetEnd = targetTimeMinutes + durationMinutes;

        for (const item of allScheduleItems) {
            // Skip the current item being checked
            if (!item || item.id === scheduleEntry.id) {
                continue;
            }

            // Check if the item is on the same day
            if (item.day !== targetDay) {
                continue;
            }

            // Check if the item is active for the target week
            const isActive = item.isActiveForWeekSync 
                ? item.isActiveForWeekSync(targetWeekNumber, targetYear)
                : true; // Fallback if sync method not available
            
            if (!isActive) {
                continue;
            }

            // Check if this item has the same teacher
            if (item.teacher && item.teacher === scheduleEntry.teacher) {
                // Check for time overlap
                const itemTime = item.parseTime();
                if (itemTime.startMinutes !== null && itemTime.endMinutes !== null) {
                    if (targetStart < itemTime.endMinutes && targetEnd > itemTime.startMinutes) {
                        const conflictTime = item.timeRangeDisplay();
                        const conflictCourse = item.course || item.type || 'another class';
                        const conflictRoom = item.room || 'unknown room';
                        
                        conflicts.push({
                            reason: `Teacher (${scheduleEntry.teacher}) has conflicting class: ${conflictCourse} in ${conflictRoom} ${conflictTime}, week ${targetWeekNumber}`,
                            severity: 'red'
                        });
                    }
                }
            }
        }

        return conflicts;
    }

    /**
     * Check if a room is valid for a subject (optimized with lookup map)
     */
    private checkSubjectRoomConstraint(subjectId: string, targetRoom: string): boolean {
        const subject = this.subjectIdLookupMap.get(subjectId);
        
        if (!subject || !Array.isArray(subject.allowedRooms) || subject.allowedRooms.length === 0) {
            return true;
        }
        const isValid = subject.allowedRooms.includes(targetRoom);
        return isValid;
    }

    /**
     * Check holiday availability for a specific day and week
     * @param targetDay - The day of the week (e.g., 'monday', 'tuesday')
     * @param targetWeekNumber - The week number
     * @param targetYear - The year
     * @returns Object with holiday info and conflict severity
     */
    private checkHolidayAvailability(
        targetDay: string,
        targetWeekNumber: number,
        targetYear: number
    ): { isHoliday: boolean; severity: Severity | null; conflictReason: ConflictReason | null } {

        try {
            const academicYearService = AcademicYearService.getInstance();
            
            // Check if the academic year structure is cached
            const cachedStructure = (academicYearService as any).cachedStructure;
            if (!cachedStructure || !cachedStructure.holidays) {
                // No cached holiday data available, can't check synchronously
                console.warn('[AvailabilityService] No cached holiday data available for synchronous check');
                return { isHoliday: false, severity: null, conflictReason: null };
            }

            // Calculate the specific date for this day
            const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
                .indexOf(targetDay.toLowerCase());
            
            if (dayIndex === -1) {
                console.warn(`[AvailabilityService] Invalid day of week: ${targetDay}`);
                return { isHoliday: false, severity: null, conflictReason: null };
            }

            // Calculate the target date using the same logic as AcademicYearService
            const jan4 = new Date(targetYear, 0, 4);
            const dayOfWeekJan4 = jan4.getDay() || 7;
            const week1Monday = new Date(jan4);
            week1Monday.setDate(jan4.getDate() - dayOfWeekJan4 + 1);

            const targetMonday = new Date(week1Monday);
            targetMonday.setDate(week1Monday.getDate() + (targetWeekNumber - 1) * 7);

            const targetDate = new Date(targetMonday);
            if (dayIndex === 0) {
                // Sunday
                targetDate.setDate(targetMonday.getDate() + 6);
            } else {
                // Monday (1) to Saturday (6)
                targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
            }

            // Normalize target date to start of day
            targetDate.setHours(0, 0, 0, 0);

            // Check against cached holidays
            for (const holiday of cachedStructure.holidays) {
                const startDate = new Date(holiday.startDate);
                const endDate = new Date(holiday.endDate);
                
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);

                if (targetDate >= startDate && targetDate <= endDate) {
                    // Determine severity based on holiday type
                    const holidayType = holiday.type?.toLowerCase() || '';
                    let severity: Severity;
                    
                    if (holidayType.includes('break') || holidayType.includes('vacation')) {
                        severity = 'orange';
                    } else {
                        // Regular holidays (public holidays, etc.)
                        severity = 'red';
                    }

                    const conflictReason: ConflictReason = {
                        reason: `${holiday.name} (${holiday.type})`,
                        severity: severity
                    };

                    return { isHoliday: true, severity, conflictReason };
                }
            }

            return { isHoliday: false, severity: null, conflictReason: null };

        } catch (error) {
            console.error('[AvailabilityService] Error checking holiday availability:', error);
            return { isHoliday: false, severity: null, conflictReason: null };
        }
    }

    /**
     * Get status for a time slot based on preferences
     */
    private getStatusForSlot(day: string, targetMinutes: number, preferenceRanges: Preference[]): StatusConstant {
        if (targetMinutes === null || !Array.isArray(preferenceRanges)) {
            return this.STATUS_CONSTANTS.FREE;
        }

        let effectiveStatus: StatusConstant = this.STATUS_CONSTANTS.FREE;

        for (const range of preferenceRanges) {
            if (range.day !== day) continue;

            const isAllDay = !range.startTime && !range.endTime;

            if (isAllDay) {
                if (range.status === this.STATUS_CONSTANTS.CANNOT) {
                    return this.STATUS_CONSTANTS.CANNOT;
                } else if (range.status === this.STATUS_CONSTANTS.PREFER_AWAY) {
                    effectiveStatus = this.STATUS_CONSTANTS.PREFER_AWAY;
                }
                continue;
            }

            const startMinutes = this.timeToMinutes(range.startTime || null);
            const endMinutes = this.timeToMinutes(range.endTime || null);

            if (startMinutes !== null && endMinutes !== null) {
                if (targetMinutes >= startMinutes && targetMinutes < endMinutes) {
                    if (range.status === this.STATUS_CONSTANTS.CANNOT) {
                        return this.STATUS_CONSTANTS.CANNOT;
                    } else if (range.status === this.STATUS_CONSTANTS.PREFER_AWAY) {
                        effectiveStatus = this.STATUS_CONSTANTS.PREFER_AWAY;
                    }
                }
            }
        }

        return effectiveStatus;
    }

    /**
     * Deduplicate conflict reasons by removing duplicate entries across weeks
     * Prioritizes red severity over orange for the same reason
     */
    private deduplicateConflictReasons(conflictReasons: ConflictReason[]): ConflictReason[] {
        const reasonMap = new Map<string, ConflictReason>();
        
        for (const reasonObj of conflictReasons) {
            if (reasonObj && reasonObj.reason) {
                // Create a key based on the base reason text (without week information)
                // Remove week-specific information to properly deduplicate
                let baseReason = reasonObj.reason
                    .replace(/\(week \d+\)/gi, '')           // Remove (week 42)
                    .replace(/week \d+\//gi, '')             // Remove week 42/
                    .replace(/,\s*week \d+/gi, '')           // Remove , week 42
                    .replace(/\s+week \d+\s*/gi, ' ')        // Remove standalone week numbers
                    .replace(/\s{2,}/g, ' ')                 // Replace multiple spaces with single space
                    .trim();

                // Create a normalized key for comparison (case-insensitive, remove extra punctuation)
                const normalizedKey = baseReason.toLowerCase()
                    .replace(/[.,;:!?]+$/, '')               // Remove trailing punctuation
                    .trim();
                
                const key = `${normalizedKey}-${reasonObj.severity}`;
                
                // Only add if not already present, prioritizing red over orange
                if (!reasonMap.has(key) ||
                    (reasonObj.severity === 'red' && reasonMap.get(key)?.severity === 'orange')) {
                    reasonMap.set(key, {
                        reason: baseReason,
                        severity: reasonObj.severity,
                        week: reasonObj.week,
                        year: reasonObj.year
                    });
                }
            }
        }
        
        return Array.from(reasonMap.values());
    }

    /**
     * Convert time string to minutes
     */
    private timeToMinutes(timeString: string | null): number | null {
        if (!timeString) return null;

        const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
        const match = timeString.match(timeRegex);

        if (match) {
            const hours = parseInt(match[1] || '0', 10);
            const minutes = parseInt(match[2] || '0', 10);

            if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
                return hours * 60 + minutes;
            }
        }
        return null;
    }

    /**
     * Get availability for a specific week
     */
    getWeekAvailability(
        scheduleEntry: ScheduleEntry,
        weekStartDate: Date,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[],
        activeDays?: string[]
    ): WeeklyAvailability {
        const weekNumber = getWeekNumber(weekStartDate);
        const year = weekStartDate.getFullYear();
        const availabilityByDay: { [key: string]: { [timeSlot: string]: AvailabilityResult } } = {};

        // Use provided activeDays or fallback to all days if not provided
        const daysToCheck = activeDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        daysToCheck.forEach((day: string) => {
            availabilityByDay[day] = {};
            
            timeSlots.forEach((timeSlot: string) => {
                const timeParts = timeSlot.split(':').map(Number);
                const hours = timeParts[0];
                const minutes = timeParts[1];
                
                if (hours === undefined || minutes === undefined) {
                    console.warn('Invalid time slot format:', timeSlot);
                    return;
                }
                
                const timeInMinutes = hours * 60 + minutes;

                const availability = this.checkAvailability(
                    scheduleEntry,
                    scheduleEntry.room,
                    day,
                    timeInMinutes,
                    allScheduleItems,
                    allTeachers,
                    allGroups,
                    allSubjects
                );

                const dayAvailability = availabilityByDay[day];
                if (dayAvailability) {
                    dayAvailability[timeSlot] = availability;
                }
            });
        });

        return {
            weekStartDate,
            weekNumber,
            availabilityByDay
        };
    }

    /**
     * Get availability for the current week
     */
    getCurrentWeekAvailability(
        scheduleEntry: ScheduleEntry,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[],
        activeDays?: string[]
    ): WeeklyAvailability {
        const currentDate = new Date();
        const weekStartDate = getWeekStartDate(currentDate);
        return this.getWeekAvailability(scheduleEntry, weekStartDate, allScheduleItems, allTeachers, allGroups, allSubjects, activeDays);
    }

    /**
     * Check if a specific time slot is available
     */
    checkTimeSlotAvailability(
        scheduleEntry: ScheduleEntry,
        day: string,
        timeSlot: string,
        allScheduleItems: ScheduleEntry[],
        allTeachers: Teacher[],
        allGroups: Group[],
        allSubjects: Subject[],
        targetWeekNumber?: number,
        targetYear?: number
    ): AvailabilityResult {
        const timeParts = timeSlot.split(':').map(Number);
        const hours = timeParts[0];
        const minutes = timeParts[1];
        
        if (hours === undefined || minutes === undefined) {
            throw new Error(`Invalid time slot format: ${timeSlot}`);
        }
        
        const timeInMinutes = hours * 60 + minutes;

        return this.checkAvailability(
            scheduleEntry,
            scheduleEntry.room,
            day,
            timeInMinutes,
            allScheduleItems,
            allTeachers,
            allGroups,
            allSubjects
        );
    }
}

export const availabilityService = new AvailabilityService(); 